import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";






import { useCartStore } from "@/stores/cartStore";
import { PRODUCTS_QUERY, storefrontApiRequest, type ShopifyProduct } from "@/lib/shopify";
import { SiteHeader } from "@/components/SiteHeader";
import MagnifierCarousel from "@/components/MagnifierCarousel";
import ParallaxCollage from "@/components/ParallaxCollage";
import collageFerryWindow from "@/assets/collage-ferry-window.jpg.asset.json";
import collageSilver from "@/assets/collage-Silver.png.asset.json";
import collageBlue2 from "@/assets/collage-WebBlue2.png.asset.json";
import collageBlue3 from "@/assets/collage-WebBlue3.png.asset.json";
import collageBlue4 from "@/assets/collage-Webblue4.png.asset.json";
import collageFerry from "@/assets/collage-8.jpg.asset.json";
import collageBanner3 from "@/assets/collage-dlbanner3.mp4.asset.json";
import carouselHongkong from "@/assets/carousel-hongkong.jpg.asset.json";
import carouselCargoShip from "@/assets/carousel-cargo-ship.jpg.asset.json";
import carouselStBarts from "@/assets/carousel-stbarts.jpg.asset.json";
import homeOspreyAsset from "@/assets/home-container-ship-1600.jpg.asset.json";
import homeOspreyWebp from "@/assets/home-container-ship-1600.webp.asset.json";
import homeOspreyAvif from "@/assets/home-container-ship-1600.avif.asset.json";
import homeHarborAsset from "@/assets/home-shrimp-harbor.jpg.asset.json";
import homeSurfAsset from "@/assets/home-surf-lineup-1600.jpg.asset.json";
import homeSurfWebp from "@/assets/home-surf-lineup-1600.webp.asset.json";
import homeSurfAvif from "@/assets/home-surf-lineup-1600.avif.asset.json";
import { Loader2 } from "lucide-react";
import heroAsset from "@/assets/hero-poster-1600.jpg.asset.json";
import heroVideo from "@/assets/web-hero.mp4.asset.json";
import heroVideoMobile from "@/assets/web-hero-mobile.mp4.asset.json";
import logoAsset from "@/assets/double-lion-logo-720.png.asset.json";
const logoUrl = logoAsset.url;
const heroWatch = heroAsset.url;
import archive1Asset from "@/assets/stacked-binoculars-left-1600.jpg.asset.json";
import archive1WebpAsset from "@/assets/stacked-binoculars-left-1600.webp.asset.json";
import archive1AvifAsset from "@/assets/stacked-binoculars-left-1600.avif.asset.json";
const archive1 = archive1Asset.url;
import archive2Asset from "@/assets/helm-binocular-1600.jpg.asset.json";
import archive2WebpAsset from "@/assets/helm-binocular-1600.webp.asset.json";
import archive2AvifAsset from "@/assets/helm-binocular-1600.avif.asset.json";
const archive2 = archive2Asset.url;
import productLoopAsset from "@/assets/product-loop-opt.mp4.asset.json";
import coconutClimberAsset from "@/assets/coconut-climber.jpg.asset.json";
import stackedTrioAsset from "@/assets/stacked-binoculars-trio.jpg.asset.json";
import { SPEC_HIGHLIGHTS } from "@/lib/specs";
import { cmsQueryOptions, field, richText } from "@/lib/cms";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Limited Edition Optics for Everyday Use and Expeditions. Made in Japan." },
      { name: "description", content: "Limited Edition Optics for Everyday Use and Expeditions. Made in Japan." },
      { property: "og:title", content: "Limited Edition Optics for Everyday Use and Expeditions. Made in Japan." },
      { property: "og:description", content: "Limited Edition Optics for Everyday Use and Expeditions. Made in Japan." },
      { property: "og:url", content: "https://doublelion.com/" },
      { property: "og:image", content: `https://doublelion.com${heroAsset.url}` },
      { property: "twitter:image", content: `https://doublelion.com${heroAsset.url}` },
    ],
    links: [
      { rel: "canonical", href: "https://doublelion.com/" },
      // Preload the hero poster (LCP candidate — first paint before video decodes).
      { rel: "preload", as: "image", href: heroAsset.url, fetchPriority: "high" },
    ],
  }),
  component: Index,
});

function Index() {
  const { data, isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const res = await storefrontApiRequest(PRODUCTS_QUERY, { first: 12, query: null });
      return (res?.data?.products?.edges ?? []) as ShopifyProduct[];
    },
  });
  const { data: cms } = useQuery(cmsQueryOptions);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const products = data ?? [];
  const featured = products[0];

  const specs = cms?.specs?.length
    ? cms.specs.map((s) => ({ value: field(s, "value", ""), label: field(s, "label", "") }))
    : SPEC_HIGHLIGHTS;

  const addItem = useCartStore((s) => s.addItem);
  const getCheckoutUrl = useCartStore((s) => s.getCheckoutUrl);
  const cartLoading = useCartStore((s) => s.isLoading);
  const [preordering, setPreordering] = useState(false);
  const heroVideoRef = useRef<HTMLVideoElement>(null);


  const handlePreorder = async () => {
    if (!featured) return;
    setPreordering(true);
    const win = window.open("", "_blank");
    try {
      const variant = featured.node.variants.edges[0]?.node;
      if (!variant) return;
      await addItem({
        product: featured,
        variantId: variant.id,
        variantTitle: variant.title,
        price: variant.price,
        quantity: 1,
        selectedOptions: variant.selectedOptions || [],
      });
      const url = getCheckoutUrl();
      if (url && win) win.location.href = url;
      else if (url) window.open(url, "_blank");
      else if (win) win.close();
    } finally {
      setPreordering(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      {/* HERO */}
      <section className="relative mt-14 mx-3 md:mx-6 mb-3 md:mb-6 h-[calc(100vh-6.5rem)] min-h-[600px] overflow-hidden bg-[#1a1f24]">
        <video
          ref={heroVideoRef}
          poster={field(cms?.hero, "background_image", heroWatch)}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={heroVideo.url} type="video/mp4" media="(min-width: 1280px)" />
          <source src={heroVideoMobile.url} type="video/mp4" />
        </video>
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 w-[min(80vw,17rem)]">
          {featured ? (
            <Link
              to="/product/$handle"
              params={{ handle: featured.node.handle }}
              className="group block bg-background text-foreground px-4 pt-4 pb-3"
            >
              <h1 className="font-sans text-sm md:text-base leading-tight tracking-tight">
                <span className="block text-left">6x16 Binoculars</span>
                <span className="block text-right">Made in Japan</span>
              </h1>
              <div className="mt-3 pt-2 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em]">
                <span>Purchase</span>
                <span className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">↗</span>
              </div>
            </Link>
          ) : (
            <div className="bg-background text-foreground px-4 pt-4 pb-3">
              <h1 className="font-sans text-sm md:text-base leading-tight tracking-tight">
                <span className="block text-left">6x16 Binoculars</span>
                <span className="block text-right">Made in Japan</span>
              </h1>
              <div className="mt-3 pt-2 border-t border-border flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em]">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Purchase
                </span>
                <span>↗</span>
              </div>
            </div>
          )}
        </div>



      </section>

      {/* COLOR VARIANTS */}
      <section className="px-6 py-24 md:py-32 border-b border-border">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 md:mb-24 text-center">
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">
              6x16 Aluminum Binoculars
            </h2>
            <p className="mt-3 font-serif text-2xl md:text-3xl tracking-tight text-muted-foreground">
              Made in Japan
            </p>
          </div>

          {featured ? (
            <div className="mx-auto max-w-4xl grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-10">
              {featured.node.variants.edges.map((edge) => {
                const variant = edge.node;
                const colorOption = variant.selectedOptions.find((o) => o.name.toLowerCase() === "color")?.value;
                if (!colorOption) return null;
                const image = variant.image?.url || featured.node.images.edges[0]?.node.url;
                return (
                  <Link
                    key={variant.id}
                    to="/product/$handle"
                    params={{ handle: featured.node.handle }}
                    search={{ color: colorOption }}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] bg-muted/40 overflow-hidden transition-transform duration-700 ease-out group-hover:-translate-y-1.5">
                      {image && (
                        <img
                          src={`${image}${image.includes("?") ? "&" : "?"}width=900`}
                          srcSet={`${image}${image.includes("?") ? "&" : "?"}width=600 600w, ${image}${image.includes("?") ? "&" : "?"}width=900 900w, ${image}${image.includes("?") ? "&" : "?"}width=1200 1200w`}
                          sizes="(min-width: 1024px) 30vw, (min-width: 768px) 45vw, 90vw"
                          alt={`${featured.node.title} — ${colorOption}`}
                          loading="lazy"
                          decoding="async"
                          width={900}
                          height={1200}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                        />
                      )}
                    </div>
                    <div className="mt-5 flex items-baseline justify-between gap-4 pb-3 border-b border-border/70 group-hover:border-foreground/60 transition-colors duration-500">
                      <h3 className="font-serif text-2xl md:text-[28px] leading-none tracking-tight text-foreground">
                        {colorOption}
                      </h3>
                      <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground transition-transform duration-500 group-hover:translate-x-1 group-hover:text-foreground">
                        View →
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>

          ) : (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

        </div>
      </section>

      {/* IMAGE BAND — scroll-linked parallax collage */}
      <ParallaxCollage
        rowOne={[
          { src: collageSilver.url, alt: "Silver 6x16 binocular on blue", span: 0.7 },
          { src: collageFerryWindow.url, alt: "Traveler glassing a fjord through a ferry window", span: 1.2 },
          { src: collageBanner3.url, alt: "Double Lion binoculars in the field", span: 0.8, video: true },
          { src: archive2, alt: "Binocular resting on a ship's helm", span: 1.1 },
          { src: collageBlue4.url, alt: "Silver binocular eyepieces on blue", span: 0.9 },
        ]}
        rowTwo={[
          { src: collageBlue2.url, alt: "Objective lenses head-on", span: 1.1 },
          { src: collageFerry.url, alt: "Traveler glassing a fjord from a ferry deck", span: 1.3 },
          { src: collageBlue3.url, alt: "Engraved specifications on the bridge plate", span: 0.9 },
          { src: carouselStBarts.url, alt: "Island coastline from above", span: 1.2 },
          { src: homeHarborAsset.url, alt: "Shrimp boats in harbor", span: 0.8 },
        ]}
      />

      {/* ABOUT + BACKSTORY */}
      <section id="about" className="px-6 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-8">
            {field(cms?.about, "section_number", "01 — About")}
          </div>
          <p className="font-serif text-3xl md:text-5xl leading-[1.2] tracking-tight max-w-3xl">
            {richText(cms?.about?.headline, "Hardware that's designed to last and observe.")}
          </p>
          <p className="mt-6 font-sans text-base md:text-lg text-foreground/70 leading-relaxed whitespace-pre-line max-w-3xl">
            {richText(
              cms?.about?.short_intro,
              "Double Lion Limited Edition binoculars strip the form back to its essentials:\nmachined aluminum, durable anodized finish, the highestquality glass, and an ultra-light go everywhere design.",
            )}
          </p>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
          >
            {field(cms?.about, "cta_label", "ABOUT US →")}
          </Link>

          <div id="story" className="mt-12 md:mt-16 pt-10 border-t border-border grid md:grid-cols-[1fr_2fr] gap-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              {field(cms?.story, "section_number", "02 — Backstory")}
            </div>
            <div className="space-y-6 font-sans text-base md:text-lg text-foreground/80 leading-relaxed">
              <p className="whitespace-pre-line">
                {richText(
                  cms?.story?.short_intro,
                  "Double Lion was founded on a premise: the best objects do one thing exceptionally well. Named by the two brothers that started this company for the heritage of their name and love for the elements. ",
                )}
              </p>
              <Link
                to="/story"
                className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
              >
                Read the backstory →
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* MAGNIFIED VIEWER */}
      <section className="hidden md:block px-6 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-8 text-center">
            MADE FOR OBSERVATION ACROSS MEDIUMS AND GEOGRAPHIES
          </div>
          <MagnifierCarousel
            zoom={3}
            images={[
              { display: homeOspreyAsset.url, hires: homeOspreyAsset.url },
              { display: carouselHongkong.url, hires: carouselHongkong.url },
              { display: carouselStBarts.url, hires: carouselStBarts.url },
              { display: homeSurfAsset.url, hires: homeSurfAsset.url },
            ]}
          />
        </div>
      </section>

      {/* SPEC HIGHLIGHTS */}
      <section id="specs" className="px-6 py-16 md:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
            03 — By the numbers
          </div>
          <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-10 max-w-3xl">
            Built small, <em className="italic font-light">{"\u00a0"}measured precisely</em>.
          </h2>

          <dl className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
            {specs.map((s) => (
              <div key={s.label} className="bg-background p-8 md:p-10 flex flex-col gap-3">
                <dd className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
                  {s.value}
                </dd>
                <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                  {s.label}
                </dt>
              </div>
            ))}
          </dl>

          {featured && (
            <div className="mt-12">
              <Link
                to="/product/$handle"
                params={{ handle: featured.node.handle }}
                className="inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
              >
                SEE THE FULL SPECIFICATIONS →
              </Link>
            </div>
          )}
        </div>
      </section>


      {/* PRODUCTS / BUY */}
      <section id="buy" className="px-6 md:px-12 py-16 md:py-24 border-t border-border">
        <div className="w-full">
          <div className="mb-12">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
              04 — Available
            </div>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">The 6×16 Binoculars</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : products.length === 0 ? (
            <div className="border border-dashed border-border py-24 text-center">
              <p className="font-serif text-2xl mb-2">No products found</p>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                Tell the chat what to add — name and price.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {products.map((p) => (
                <ProductCard key={p.node.id} product={p} featured={p === featured} />
              ))}
              <div className="rounded-2xl overflow-hidden ring-1 ring-border/60 bg-card">
                <video
                  src={productLoopAsset.url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  tabIndex={-1}
                  width={720}
                  height={1280}
                  className="w-full h-full object-cover min-h-[420px]"
                />
              </div>
              <div className="rounded-2xl overflow-hidden ring-1 ring-border/60 bg-card">
                <img
                  src={coconutClimberAsset.url}
                  alt="Double Lion 6×16 binoculars in a tropical setting"
                  loading="lazy"
                  decoding="async"
                  width={1600}
                  height={2400}
                  className="w-full h-full object-cover min-h-[420px]"
                />
              </div>
            </div>

          )}
        </div>
      </section>






    </div>
  );
}

function ProductCard({ product, featured }: { product: ShopifyProduct; featured?: boolean }) {
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const node = product.node;
  const variant = node.variants.edges[0]?.node;
  const image = node.images.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;

  const handleAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!variant) return;
    const win = window.open("", "_blank");
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    const url = useCartStore.getState().checkoutUrl;
    if (url) {
      if (win) win.location.href = url;
      else window.open(url, "_blank");
    } else if (win) {
      win.close();
    }
  };

  return (
    <Link
      to="/product/$handle"
      params={{ handle: node.handle }}
      className="group relative bg-card rounded-2xl overflow-hidden ring-1 ring-border/60 hover:ring-foreground/40 shadow-[0_1px_2px_rgba(0,0,0,0.04)] hover:shadow-[0_24px_48px_-24px_rgba(0,0,0,0.25)] transition-all duration-500 hover:-translate-y-1 flex flex-col"
    >
      <div className="aspect-square bg-muted overflow-hidden">
        <img
          src={featured ? stackedTrioAsset.url : (image?.url ?? "")}
          alt={image?.altText || node.title}
          loading="lazy"
          decoding="async"
          width={featured ? 1600 : 800}
          height={featured ? 1000 : 800}
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
        />
      </div>
      <div className="p-6 md:p-8 flex-1 flex flex-col">
        <div className="flex items-baseline justify-between gap-4 mb-2">
          <h3 className="font-serif text-2xl tracking-tight">{node.title}</h3>
          <span className="font-mono text-sm whitespace-nowrap tabular-nums">
            {price.currencyCode} {parseFloat(price.amount).toFixed(0)}
          </span>
        </div>
        {node.description && (
          <p className="font-sans text-sm text-muted-foreground line-clamp-2 mt-1">{node.description}</p>
        )}
        <div className="mt-5">
          <button
            onClick={handleAdd}
            disabled={isLoading || !variant}
            className="inline-flex items-center justify-center h-8 px-4 rounded-full bg-foreground text-background font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-foreground/90 disabled:opacity-40 transition-colors"
          >
            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add to Bag"}
          </button>
        </div>
      </div>
    </Link>
  );
}

